<template>
  <div id="contract-list">
    <b-row align-h="between">
      <b-col cols="8">
        <div id="title">{{ $t('CONTRACT_MANAGEMENT') }}</div>
        <ul class="summary">
          <li>{{ $t('PENDING') }}: <span class="value">{{ summary.pending }}</span></li>
          <li>{{ $t('APPROVED') }}: <span class="value">{{ summary.aproved }}</span></li>
          <li>{{ $t('DISBURSED') }}: <span class="value">{{ summary.disbursed }}</span></li>
          <li>{{ $t('OVERDUE') }}: <span class="value">{{ summary.overdue }}</span></li>
          <li>{{ $t('RENEWED') }}: <span class="value">{{ summary.renewed }}</span></li>
          <li>{{ $t('REPAID') }}: <span class="value">{{ summary.repaid }}</span></li>
        </ul>
        <ul class="summary">
          <li>{{ $t('CLIENTS') }}: <span class="value">{{ summary.clients }}</span></li>
        </ul>
      </b-col>
      <b-col cols="4">
        <div class="d-flex justify-content-end">
          <filter-box />
          <date-filter
            pure-icon
            white
            :from="filters.fromDate"
            :to="moment().format(DATE_FORMAT)"
            :presets="datePresets"
            @filter="setDate"
          />
          <text-input
            :placeholder="$t('INPUT_CONTRACT_ID_OR_CLIENT_NAME_PHONE_NUMBER_ASSET_DESCRIPTION')"
            v-model="searchInput"
            rounded-edge
            with-shadow
            borderless
            @keydown.enter="search"
          >
            <template v-slot:icon>
              <font-awesome-icon class="icon" :icon="faSearch" />
            </template>
          </text-input>
        </div>
      </b-col>
    </b-row>
    <div class="list" ref="list">
      <table>
        <thead>
          <tr>
            <th colspan="2">{{ $t('CNTRCT_ID') }}</th>
            <th>{{ $t('CREATED_DATE') }}</th>
            <th>{{ $t('MATURE_DATE') }}</th>
            <th>{{ $t('CLIENT') }}</th>
            <th>{{ $t('ASSET') }}</th>
            <th>{{ $t('LOAN_AMOUNT') }}</th>
            <th>{{ $t('INTEREST_RATE') }}</th>
            <th>{{ $t('STORAGE') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr :key="key" v-for="(item, key) in items" @click="navigateContractDetail(item.finfluxId)">
            <td
              class="status"
              :class="{
                'pending': (item.status === CONTRACT_STATUSES.PENDING),
                'approved': (item.status === CONTRACT_STATUSES.APPROVED),
                'disbursed': (item.status === CONTRACT_STATUSES.DISBURSED),
                'closed': (item.status === CONTRACT_STATUSES.CLOSED),
                'overdue': (item.status === CONTRACT_STATUSES.OVERDUE),
                'rejected': (item.status === CONTRACT_STATUSES.REJECTED)
              }"
            ><div class="bullet">•</div></td>
            <td
              class="status"
              :class="{
                'pending': (item.status === CONTRACT_STATUSES.PENDING),
                'approved': (item.status === CONTRACT_STATUSES.APPROVED),
                'disbursed': (item.status === CONTRACT_STATUSES.DISBURSED),
                'closed': (item.status === CONTRACT_STATUSES.CLOSED),
                'overdue': (item.status === CONTRACT_STATUSES.OVERDUE),
                'rejected': (item.status === CONTRACT_STATUSES.REJECTED)
              }"
            >
              <router-link :to="applyParams(PATHS.CONTRACT_DETAIL, item.finfluxId)">{{ item.finfluxId }}</router-link>
              <br />
              <small>{{ $t(item.status) }}</small>
            </td>
            <td>{{ formatDate(item.submittedOn) }}</td>
            <td>{{ formatDate(item.maturesOn) }}</td>
            <td>{{ item.client.nameVi }}</td>
            <td class="asset" v-html="getFullDescription(item.asset)" />
            <td>{{ addCommas(item.approvedAmount) }}</td>
            <td>{{ item.interestRate }}</td>
            <td>{{ item.asset.storage.name }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      :page="filters.page"
      :pageSize="filters.pageSize"
      :total="total"
      @pageSizeChange="changePageSize"
      @pageChange="changePage"
    />
  </div>
</template>

<script>
import { ref, onMounted, watch } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

import { addCommas, applyParams, camelToUpperSnake, scrollToTop } from '@/helpers';
import { CONTRACT_STATUSES, PATHS, DATE_FORMAT } from '@/consts';
import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';
import DateFilter from '@/components/DateFilter';
import FilterBox from './FilterBox';

const { useState, useActions } = createNamespacedHelpers('contractList');

const now = moment();

const datePresets = [
  {
    text: 'TODAY',
    range: [now.format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: 'THIS_MONTH',
    range: [now.clone().startOf('month').format(DATE_FORMAT), now.format(DATE_FORMAT)]
  },
  {
    text: 'LAST_MONTH',
    range: [now.clone().subtract(1, 'month').startOf('month').format(DATE_FORMAT), now.clone().subtract(1, 'month').endOf('month').format(DATE_FORMAT)]
  },
  {
    text: 'THIS_YEAR',
    range: [now.clone().startOf('year').format(DATE_FORMAT), now.format(DATE_FORMAT)]
  }
];

export default {
  name: 'ContractList',
  components: {
    TextInput,
    FontAwesomeIcon,
    Pagination,
    FilterBox,
    DateFilter
  },
  metaInfo() {
    const { $i18n } = this;
    const title = $i18n.t('CONTRACT_MANAGEMENT');

    return {
      title,
      meta: []
    };
  },
  setup(_, { root }) {
    const list = ref(null);

    const { filters, items, total, summary } = useState(['filters', 'items', 'total', 'summary']);

    const searchInput = ref(filters.value.term);

    const { applyFilter, getContractList } = useActions(['applyFilter', 'getContractList']);

    const changePageSize = (value) => {
      applyFilter({
        pageSize: value,
        page: 1,
      });
    };

    const changePage = (value) => {
      applyFilter({
        page: value,
      });
    };

    const search = () => {
      applyFilter({
        term: searchInput.value,
        page: 1
      });
    };

    const formatDate = (date) => {
      const formatedDate = moment(date, 'YYYY-MM-DD');
      if (formatedDate.isValid()) {
        return formatedDate.format('DD/MM/YYYY');
      }
      return '';
    };

    const checkStorage = (branch) => (branch === null ? '' : branch.branchName);
    const checkClient = (client) => (client === null ? '' : client.name);

    onMounted(() => {
      getContractList();
    });

    watch(filters, () => getContractList());
    watch(items, () => scrollToTop(list.value));

    const navigateContractDetail = (id) => {
      root.$router.push({ name: 'contract-detail', params: { id } }).catch(() => {});
    };

    const getFullDescription = (asset) => {
      let desc = asset.description;

      Object.keys(asset.meta || {}).forEach((key) => {
        if (asset.meta[key]) {
          desc += `<br/><span style="color:#BCBCBC">${root.$t(camelToUpperSnake(key))}:</span> <span>${asset.meta[key]}</span>`;
        }
      });

      return desc;
    };

    const setDate = (from, to) => {
      applyFilter({
        fromDate: from,
        toDate: to,
        page: 1
      });
    };

    return {
      faSearch,
      searchInput,
      search,
      filters,
      items,
      total,
      changePageSize,
      changePage,
      formatDate,
      checkStorage,
      checkClient,
      navigateContractDetail,
      addCommas,
      CONTRACT_STATUSES,
      PATHS,
      applyParams,
      getFullDescription,
      summary,
      list,
      datePresets,
      setDate,
      moment,
      DATE_FORMAT
    };
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#contract-list {
  padding: 32px 45px;

  #title {
    font-size: 28px;
    margin-bottom: 0.5rem;
  }

  .toggle-box {
    margin-right: 5px;
    .toggle-button {
      border-radius: 4px;
    }
  }

  .summary {
    padding: 0;
    margin: 0;
    margin-bottom: 3px;
    list-style: none;

    li {
      display: inline-block;
      margin-right: 15px;
      font-weight: bold;

      .value {
        color: $paradisePink;
      }
    }
  }

  .list {
    margin-top: 20px;
    margin-bottom: 20px;
    max-height: calc(100vh - 320px);
    overflow-y: auto;

    table {
      width: 100%;
      border-collapse: inherit;
      border-spacing: 0;

      thead {
        text-transform: uppercase;
        font-weight: bold;
        text-align: center;

        tr {
          background-color: $babyPowder;

          th {
            padding: 15px 10px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;
            position: sticky;
            top: 0;
            z-index: 9;
            background-color: $babyPowder;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }
          }
        }
      }

      tbody {
        text-align: center;

        tr {
          background-color: $babyPowder;
          cursor: pointer;
          transition: all .2s ease-in-out;

          &:hover {
            background-color: $ghostWhite;
          }

          td {
            padding: 15px 5px;
            border-top: 1px solid $alto;
            border-bottom: 1px solid $alto;

            &:first-child {
              border-top-left-radius: 3px;
              border-bottom-left-radius: 3px;
              border-left: 1px solid $alto;
              color: $geranium;
            }

            &:last-child {
              border-top-right-radius: 3px;
              border-bottom-right-radius: 3px;
              border-right: 1px solid $alto;
            }

            &.asset {
              max-width: 400px;
              text-align: left;
            }

            a {
              color: inherit;
            }

            &.status {
              color: $independence;
              line-height: 18px;

              .bullet {
                font-size: 36px;
                padding-right: 0;
                padding-left: 10px;
                transform: translateY(-2px);
                vertical-align: middle;
                display: inline-block;
              }

              &.disbursed {
                color: $shamrockGreen;
              }

              &.closed, &.canceled {
                color: $independence;
              }

              &.overdue {
                color: $geranium;
              }

              &.rejected {
                color: $lavenderGrey;
              }

              &.pending {
                color: $yellowCrayola;
              }

              &.approved {
                color: $blueNcs;
              }
            }
          }
        }
      }
    }
  }
}
</style>
